import React from 'react';
import { Trash2 } from 'lucide-react';
import type { AutocompleteOption } from '../../../components/common/AutocompleteSelect';
import type { InventoryItem } from '../../../types/inventory';
import type { Machine } from '../../../types/machine';
import type { WorkOrder } from '../../../types/workOrder';
import type { WeightUnit } from '../../../hooks/useWeightUnit';
import { useLanguage } from '../../../features/language/useLanguage';
import { BasicInfoSection } from '../components/BasicInfoSection';
import { BeanSelectionSection } from '../components/BeanSelectionSection';
import type { BasicInfoFormData } from '../schemas/workOrderSchema';
import {
  useWorkOrderCardForm,
  type BulkCardCollected,
  type BulkCardSnapshot,
  type WorkOrderCardFormDefaults,
} from './useWorkOrderCardForm';

export type BulkStage = 'draft' | 'schedule';

export interface BulkCardHandle {
  collect: () => Promise<BulkCardCollected>;
  /** Current basic-info values, unvalidated — a new card inherits them. */
  peek: () => BasicInfoFormData;
}

interface Props {
  cardId: string;
  index: number;
  stage: BulkStage;
  workOrder: WorkOrder | null;
  defaults?: WorkOrderCardFormDefaults;
  canRemove: boolean;
  onRemove: () => void;
  greenBeans: InventoryItem[];
  machineOptions: AutocompleteOption[];
  roasterOptions: AutocompleteOption[];
  machines: Machine[];
  onCreateMachine: (text: string) => void;
  onCreateRoaster: (text: string) => void;
  canCreateMachine: boolean;
  canCreateRoaster: boolean;
  isAtMachineTypeLimit: boolean;
  /** Every card's published snapshot, this one included; the budget below
   *  subtracts the others. Passing the map (not a per-card object) keeps the
   *  reference stable between real changes, which matters at 20 cards. */
  snapshots: Record<string, BulkCardSnapshot>;
  onSnapshotChange: (cardId: string, snapshot: BulkCardSnapshot) => void;
  registerHandle: (cardId: string, handle: BulkCardHandle | null) => void;
  unit: WeightUnit;
  setUnit: (value: WeightUnit) => void;
}

export const BulkWorkOrderCard: React.FC<Props> = ({
  cardId,
  index,
  stage,
  workOrder,
  defaults,
  canRemove,
  onRemove,
  greenBeans,
  machineOptions,
  roasterOptions,
  machines,
  onCreateMachine,
  onCreateRoaster,
  canCreateMachine,
  canCreateRoaster,
  isAtMachineTypeLimit,
  snapshots,
  onSnapshotChange,
  registerHandle,
  unit,
  setUnit,
}) => {
  const { t } = useLanguage();

  const otherAllocations = React.useMemo(() => {
    const totals: Record<string, number> = {};
    for (const [id, snapshot] of Object.entries(snapshots)) {
      if (id === cardId) continue;
      for (const [lotId, qty] of Object.entries(snapshot.allocations)) {
        totals[lotId] = (totals[lotId] ?? 0) + qty;
      }
    }
    return totals;
  }, [snapshots, cardId]);

  const card = useWorkOrderCardForm({ defaults, greenBeans, otherAllocations });

  const { snapshot, collect, getValues, selectedMachineTypeId } = card;

  // The orchestrator owns the cross-card lot budget and the ready/incomplete
  // counts, but the numbers live in this card's state — publish them upward.
  React.useEffect(() => {
    onSnapshotChange(cardId, snapshot);
  }, [cardId, snapshot, onSnapshotChange]);

  // Submitting reads every card at once, so the orchestrator holds a handle
  // per card rather than lifting the whole form state out of it.
  React.useEffect(() => {
    registerHandle(cardId, { collect, peek: getValues });
    return () => registerHandle(cardId, null);
  }, [cardId, collect, getValues, registerHandle]);

  const selectedMachine = machines.find((m) => m.id === selectedMachineTypeId);

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5 shadow-sm"
      data-test-id={`wo-bulk-card-${index}`}
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">
            {workOrder?.workOrderNumber || t('workOrders.bulk.scheduleTitle', { index: index + 1 })}
          </h3>
          {workOrder && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {t('workOrders.bulk.draftBadge')}
            </span>
          )}
        </div>
        {stage === 'draft' && (
          <button
            type="button"
            onClick={onRemove}
            disabled={!canRemove}
            title={t('workOrders.bulk.removeSchedule')}
            aria-label={t('workOrders.bulk.removeSchedule')}
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
            data-test-id={`wo-bulk-remove-schedule-${index}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <BasicInfoSection
        register={card.register}
        control={card.control}
        setValue={card.setValue}
        errors={card.errors}
        initialData={workOrder || undefined}
        machineOptions={machineOptions}
        roasterOptions={roasterOptions}
        onCreateMachine={onCreateMachine}
        onCreateRoaster={onCreateRoaster}
        canCreateMachine={canCreateMachine}
        canCreateRoaster={canCreateRoaster}
        selectedMachine={selectedMachine}
        isCompleted={false}
        isAtMachineTypeLimit={isAtMachineTypeLimit}
      />

      {stage === 'schedule' && (
        <>
          {/* No RoastProfileSectionWrapper in bulk: the roast profile checkbox
              and the "Save as Complete" path do not exist here. The roast type
              selector still renders — it lives inside BeanSelectionSection
              under !useRoastProfile, and is not part of the roast profile. */}
          <BeanSelectionSection
            isEditMode
            greenBeans={greenBeans}
            selectedBeanId={card.selectedBeanId}
            handleBeanChange={card.handleBeanChange}
            handleAutoCalculateLots={card.handleAutoCalculateLots}
            isAutoCalculatingLots={card.isAutoCalculatingLots}
            manualInputWeight={card.manualInputWeight}
            manualOutputWeight={card.manualOutputWeight}
            handleWeightInputChange={card.handleWeightInputChange}
            handleWeightOutputChange={card.handleWeightOutputChange}
            unit={unit}
            setUnit={setUnit}
            lotOptions={card.lotOptions}
            lots={card.lots}
            handleLotChange={card.handleLotChange}
            handleWeightChange={card.handleWeightChange}
            addLotRow={card.addLotRow}
            removeLotRow={card.removeLotRow}
            totalUsedWeight={card.totalUsedWeight}
            parsedRoastInputWeight={card.parsedRoastInputWeight}
            isWeightMismatch={card.isWeightMismatch}
            useRoastProfile={false}
            roastType={card.roastType}
            setRoastType={card.setRoastType}
            isOutputExceedsInput={card.isOutputExceedsInput}
            isCompleted={false}
          />

          {!card.isComplete && (
            <p
              className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              data-test-id={`wo-bulk-incomplete-hint-${index}`}
            >
              {t('workOrders.bulk.incompleteHint')}
            </p>
          )}
          {card.warningMessage && <p className="mt-2 text-xs text-red-600">{card.warningMessage}</p>}
        </>
      )}
    </div>
  );
};
